import {logger, UUID} from 'yougile-util'
import {DataObjectId, Hub} from 'yougile-model'
import {Sync} from '../../client/sync'
import {ChatUtils} from '../../extensions/chat-utils'
import {globals} from '../../extensions/globals-base'
// import {Page} from '../../extensions/page';
import {ContextObject} from '../context-object'
import {Item} from './item'
import {Items} from './items'
import {UserObj} from './user-obj'
import {Project} from '@extensions/project'

type GetCurrentBoard = () => Hub | DataObjectId
type GetCurrentProject = () => Hub | DataObjectId
type CurrentAddLocationListener = (listener: () => void) => UUID
type CurrentRemoveLocationListener = (id: UUID) => void

/**
 * @description
 * Object Current contains information about the current application state.
 * @descriptionRu
 * Объект "Current" содержит информацию о текущем статусе приложения.
 * @example
 * // getting current user
 * if (Current.user.name === 'John Smith') {
 *   Notifier.success('Hi John!');
 * }
 */
export class Current extends ContextObject {
  private static getCurrentBoard: GetCurrentBoard
  private static getCurrentProject: GetCurrentProject
  private static addLocationListener: CurrentAddLocationListener
  private static removeLocationListener: CurrentRemoveLocationListener
  public static override registered: Map<DataObjectId, Map<string, Current>> =
    new Map()

  public static init(
    getCurrentBoard: GetCurrentBoard,
    getCurrentProject: GetCurrentProject,
    addLocationListener: CurrentAddLocationListener,
    removeLocationListener: CurrentRemoveLocationListener
  ): void {
    Current.getCurrentBoard = getCurrentBoard
    Current.getCurrentProject = getCurrentProject
    Current.addLocationListener = addLocationListener
    Current.removeLocationListener = removeLocationListener
  }

  /**
   * @code
   * Current.user;
   * @example
   * if (Current.user.name === 'John') {
   *   Notifier.success('Hi John!');
   * }
   * @description
   * Current user (see “user variable”).
   * @descriptionRu
   * Текущий пользователь (см. “user variable”).
   */
  public get user(): UserObj {
    return new UserObj(globals.currentUserId, this.context.box)
  }

  /**
   * @code
   * Current.project;
   * @description
   * Opened project (see “item variable”). Readonly.
   * To open another project use .open()
   * method of item variable.
   * @descriptionRu
   * Открытый проект (см. “item variable”). Только для чтения.
   * Чтобы открыть другой проект, используйте метод .open()
   * (см. “item variable”).
   */
  public get project(): Item {
    const hub = Current.getCurrentProjectHub()
    if (hub && hub.companyId !== this.context.box.companyId) {
      return undefined
    }
    const p = hub ? new Item(hub.id, this.context.box) : undefined
    return p
  }

  /**
   * @code
   * Current.projects;
   * @description
   * List of all projects in the company. Readonly.
   * @descriptionRu
   * Список всех проектов в компании. Только для чтения.
   */
  public get projects(): Item[] {
    const companyId = globals.getCompanyId()
    const allProjects = Project.listProjects(companyId, 'not-archive').sort(
      Project.projectOrderComparer
    )
    return allProjects.map(project => new Item(project.id, this.context.box))
  }

  /**
   * @code
   * Current.board;
   * @description
   * Opened board (see “item variable”). Readonly.
   * To open another board use .open()
   * method of item variable.
   * @descriptionRu
   * Открытая доска (см. “item variable”). Только для чтения.
   * Чтобы открыть другую
   * доску, используйте метод .open() (“item variable”).
   */
  public get board(): Item {
    const hub = Current.getCurrentBoardHub()
    if (hub && hub.companyId !== this.context.box.companyId) {
      return undefined
    }
    return hub ? new Item(hub.id, this.context.box) : undefined
  }

  /**
   * @code
   * Current.chat;
   * @description
   * Opened chat’s task (see “item variable”). Readonly.
   * To open another chat use
   * Chat.openChat(...) method.
   * @descriptionRu
   * Открытый чат задачи (см. “item variable”). Только для чтения.
   * Чтобы открыть
   * другой чат, используйте метод Chat.openChat(...).
   */
  public get chat(): Item {
    const hub = ChatUtils.getOpenedChatHub()
    if (hub && hub.companyId !== this.context.box.companyId) {
      return undefined
    }
    return hub ? new Item(hub.id, this.context.box) : undefined
  }

  /**
   * @code
   * Current.company;
   * @description
   * Returns current company. Script scope is restricted to one
   * company so it always remains the same. If user switches the
   * company script is unloaded.
   * @descriptionRu
   * Возвращает компанию, в которой запускается скрипт.
   * Работа скрипта ограничена одной компанией, поэтому
   * компания остаётся всегда одинаковой. Если пользователь переходит
   * в другую компанию, скрипт отгружается и запускаются скрипты другой
   * компании.
   * @example
   * var company = Current.company;
   * Notifier.success('Company is ' + company.name);
   */
  public get company(): Item {
    return new Item(this.context.box.companyId, this.context.box)
  }

  /**
   * @code
   * Current.onProjectChange = function (oldProject, newProject) {
   *
   * };
   * @example
   * Current.onProjectChange = function (x, y) {
   *   Notifier.success('Navigated to ' + y.name);
   * };
   * @description
   * This function is called when user navigates to another project.
   * If user leaves a project (e.g.: navigates to the project page) then this
   * function is called with an undefined second argument.
   * @descriptionRu
   * Эта функция вызывается, когда пользователь переходит в другой проект.
   * Если пользователь покидает проект (т.е. переходит на страницу проектов),
   * то эта функция вызывается с неопределенным (undefined) вторым аргументом.
   */
  public onProjectChange?: (oldProject: Item, newProject: Item) => void

  /**
   * @code
   * Current.onBeforeScriptUnload = () => {
   *   // disposing resources
   * }
   * @example
   * const instance = new Logic();
   * Current.onBeforeScriptUnload = function () {
   *   instance.Dispose();
   * };
   * @description
   * This function is called when the extension's script is about to be unloaded.
   * @descriptionRu
   * Эта функция вызывается, когда скрипт расширения готовится к выгрузке.
   */
  public onBeforeScriptUnload?: (oldBoard?: Item, newBoard?: Item) => void

  /**
   * @code
   * Current.onBoardChange = function (oldBoard, newBoard) {
   *
   * };
   * @example
   * Current.onBoardChange = function (x, y) {
   *   Notifier.success('Board ' + y.name + ' opened!');
   * };
   * @description
   * This function is called when user navigates to a new board.
   * @descriptionRu
   * Эта функция вызывается, когда пользователь переходит в новую доску.
   */
  public onBoardChange?: (oldBoard?: Item, newBoard?: Item) => void

  /**
   * @code
   * Current.onChatChange = function (oldChatTask, newChatTask) {
   *
   * };
   * @example
   * Current.onChatChange = function (x, y) {
   *   Notifier.success('Opened chat for ' + y.name + ' task!');
   * };
   * @description
   * This function is called when the opened chat’s task changes.
   * @descriptionRu
   * Эта функция вызывается, когда открывается чат по другой задаче.
   */
  public onChatChange?: (oldChat: Item, newChat: Item) => void

  private locListenerId?: UUID
  private chatListenerId?: UUID

  public override afterLoad(): void {
    // api:exclude
    const currIds: {
      project?: DataObjectId
      board?: DataObjectId
      chat?: DataObjectId
    } = {}
    this.chatListenerId = ChatUtils.addChatChangeListener(() => {
      try {
        const {chat, onChatChange} = this
        const currChat = chat ? chat.id : undefined

        if (currIds.chat !== currChat && onChatChange) {
          const oldChat = currIds.chat ? Items.get(currIds.chat) : undefined
          const newChat = currChat ? Items.get(currChat) : undefined

          onChatChange(oldChat, newChat)
        }
      } catch (e) {
        logger.error(e)
      }
      currIds.chat = ChatUtils.getOpenedChatHub()
        ? ChatUtils.getOpenedChatHub().id
        : undefined
    })
    this.locListenerId = Current.addLocationListener(() => {
      try {
        const {project, board, onProjectChange, onBoardChange} = this
        const currPrj = project ? project.id : undefined
        const currBrd = board ? board.id : undefined
        if (currIds.project !== currPrj && onProjectChange) {
          const oldProject = currIds.project
            ? Items.get(currIds.project)
            : undefined
          const newProject = currPrj ? Items.get(currPrj) : undefined

          onProjectChange(oldProject, newProject)
        }
        if (currIds.board !== currBrd && onBoardChange) {
          const oldBoard = currIds.board ? Items.get(currIds.board) : undefined
          const newBoard = currBrd ? Items.get(currBrd) : undefined

          onBoardChange(oldBoard, newBoard)
        }
      } catch (e) {
        logger.error(e)
      }

      currIds.project = Current.getCurrentProject()
        ? Current.getCurrentProjectHub().id
        : undefined
      currIds.board = Current.getCurrentBoard()
        ? Current.getCurrentBoardHub().id
        : undefined
    })
  }

  public override beforeUnload(): void {
    // api:exclude
    Current.removeLocationListener(this.locListenerId)
    ChatUtils.removeChatChangeListener(this.chatListenerId)

    if (this.onBeforeScriptUnload) {
      this.onBeforeScriptUnload()
    }
  }

  private static getCurrentProjectHub(): Hub {
    const project = Current.getCurrentProject()

    return project instanceof Hub
      ? project
      : (Sync.getCurrentSandbox().get(project) as Hub)
  }

  private static getCurrentBoardHub(): Hub {
    const board = Current.getCurrentBoard()

    return board instanceof Hub
      ? board
      : (Sync.getCurrentSandbox().get(board) as Hub)
  }
}
